"use client";

import React, { useState, useMemo } from "react";
import Link from "next/link";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { QuizAnswers } from "@/lib/pipeline/style-profile";
import type { GeneratedDesign } from "@/lib/pipeline/engine";
import { applyPaywallToItems, swapDesignItem, calculateTotalLookPrice } from "@/lib/pipeline/unlock";
import { Product, TagItem } from "@/types";
import { formatGBP } from "@/lib/utils";
import {
  Sparkles,
  ArrowLeft,
  ArrowRight,
  UploadCloud,
  ShieldCheck,
  CheckCircle2,
  Lock,
  Unlock,
  ExternalLink,
  RefreshCw,
  RotateCcw,
  Mail,
  X,
  Tag,
} from "lucide-react";

const STYLES_OPTIONS = [
  { id: "Warm Editorial", label: "Warm Editorial", desc: "Rich textures, brass accents, refined neutrals" },
  { id: "Japandi", label: "Japandi", desc: "Minimalist oak, clean lines, organic ceramics" },
  { id: "Modern Classic", label: "Modern Classic", desc: "Tailored upholstery, panelling, timeless symmetry" },
  { id: "Country Cottage", label: "Country Cottage", desc: "Plump seating, botanical prints, rustic timber" },
  { id: "Bold & Eclectic", label: "Bold & Eclectic", desc: "Deep jewel tones, statement silhouettes, art" },
];

const PALETTE_OPTIONS = [
  { id: "Warm Neutrals", label: "Warm Neutrals", colors: ["#FAF6EF", "#E4DCCF", "#B4653F"] },
  { id: "Earthy Clay & Sage", label: "Earthy Clay & Sage", colors: ["#B4653F", "#7C8471", "#FAF6EF"] },
  { id: "Deep Drama", label: "Deep Drama", colors: ["#211D18", "#7E683B", "#8F4E30"] },
  { id: "Fresh Air & Light", label: "Fresh Air & Light", colors: ["#FFFFFF", "#D8E2DC", "#FFE5D9"] },
];

const BUDGET_OPTIONS = [
  { id: "<1k", label: "Under £1,000", desc: "Budget-friendly refresh with high-impact hero pieces" },
  { id: "1k-2k", label: "£1,000 – £2,000", desc: "Balanced look with quality velvet seating and textiles" },
  { id: "2k-4k", label: "£2,000 – £4,000", desc: "Full cohesive transformation with designer finishes" },
  { id: "4k+", label: "£4,000+", desc: "Premium room overhaul with luxury pieces and trade paints" },
];

const KEEP_CHIPS = ["Sofa", "Bed", "Curtains", "Flooring", "Fireplace", "Existing Art", "Nothing"];
const LIFE_FACTORS = [
  { id: "kids", label: "Kids in the home" },
  { id: "pets", label: "Pets (cats/dogs)" },
  { id: "rental", label: "Rental property (no drilling)" },
  { id: "wfh", label: "Work from home setup" },
];


function TypewriterText({ text }: { text: string }) {
  const [displayed, setDisplayed] = React.useState("");
  React.useEffect(() => {
    setDisplayed("");
    let i = 0;
    const interval = setInterval(() => {
      setDisplayed(text.substring(0, i));
      i++;
      if (i > text.length) clearInterval(interval);
    }, 45); // 45ms per character gives a natural typing speed
    return () => clearInterval(interval);
  }, [text]);
  return (
    <>
      "{displayed}<span className="animate-pulse opacity-50">_</span>"
    </>
  );
}

const THEATER_MESSAGES = [
  "Analyzing room architecture and natural lighting...",
  "Identifying spatial constraints and scale...",
  "Applying your personal style preferences...",
  "Cross-referencing real inventory from our retail partners...",
  "Selecting hero pieces to anchor the space...",
  "Sourcing complementary textiles and accessories...",
  "Ensuring physical dimensions fit your footprint...",
  "Balancing the budget against premium finishes...",
  "Generating photorealistic renders of your new room...",
  "Finalizing your personalized shopping list...",
];


const DEFAULT_LIVING_ELEMENTS = [
  { name: "Paint / Wallpaper", selected: true },
  { name: "Sofa", selected: true, option: "3 seater" },
  { name: "Armchair", selected: true, option: "1" },
  { name: "Coffee Table", selected: true },
  { name: "Side Table", selected: true },
  { name: "Console Table", selected: false },
  { name: "Sideboard", selected: false },
  { name: "TV Unit", selected: false },
  { name: "Shelving", selected: false },
  { name: "Storage", selected: false },
  { name: "Rug", selected: true },
  { name: "Curtains", selected: false },
  { name: "Pendant Light", selected: true },
  { name: "Floor Lamp", selected: false },
  { name: "Table Lamp", selected: false },
  { name: "Wall Art", selected: true },
  { name: "Mirror", selected: false },
  { name: "Cushions", selected: true },
  { name: "Throws", selected: false },
  { name: "Decor", selected: true },
];

const DEFAULT_BEDROOM_ELEMENTS = [
  { name: "Paint / Wallpaper", selected: true },
  { name: "Bed Frame", selected: true, option: "Double" },
  { name: "Mattress", selected: true, option: "Double" },
  { name: "Bedding", selected: false },
  { name: "Bedside Table", selected: true, option: "2" },
  { name: "Chest of Drawers", selected: true },
  { name: "Wardrobe", selected: false },
  { name: "Storage", selected: false },
  { name: "Rug", selected: true },
  { name: "Curtains", selected: false },
  { name: "Pendant Light", selected: true },
  { name: "Table Lamp", selected: false },
  { name: "Floor Lamp", selected: false },
  { name: "Wall Art", selected: true },
  { name: "Mirror", selected: false },
  { name: "Cushions", selected: true },
  { name: "Throws", selected: false },
  { name: "Decor", selected: true },
];


export default function DesignFunnelPage() {
  const [step, setStep] = useState<"invite" | "upload" | "quiz" | "generating" | "reveal">("invite");
  const [inviteCode, setInviteCode] = useState("");
  const [quizStep, setQuizStep] = useState<number>(1);
  const [hasBedding, setHasBedding] = useState(false);
  React.useEffect(() => {
    const controller = new AbortController();
    fetch("/api/catalog?summary=1", { signal: controller.signal })
      .then(async response => { if (response.ok) setHasBedding((await response.json()).categories.includes("bedding")); })
      .catch(() => { /* Bedding stays hidden until a real catalog is available. */ });
    return () => controller.abort();
  }, []);

  // Form State
  const [roomType, setRoomType] = useState<"living" | "bedroom">("living");
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [imageAspectRatio, setImageAspectRatio] = useState<string>("16:9");
  const [quizAnswers, setQuizAnswers] = useState<QuizAnswers>({
    roomType: "living",
    styleAesthetic: ["Warm Editorial"],
    colorMood: "Warm Neutrals",
    budgetBand: "2k-4k",
    lifeFactors: [],
    approxWidthMeters: 4.0,
    elementsToAdd: DEFAULT_LIVING_ELEMENTS,
    brands: [],
    feeling: "",
  });

  // Generated Design Result & Paywall state
  const [designResult, setDesignResult] = useState<GeneratedDesign | null>(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [unlockEmail, setUnlockEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [isUnlockModalOpen, setIsUnlockModalOpen] = useState(false);

  // Item Swap Drawer State
  const [swapTargetItem, setSwapTargetItem] = useState<
    (TagItem & { product?: Product; alternatives?: Product[] }) | null
  >(null);

  const [theaterMessageIndex, setTheaterMessageIndex] = useState(0);

  const THEATER_MESSAGES = [
    "Reading room architecture & natural daylight plane...",
    "Applying style preferences & spatial constraints...",
    "Matching your selected categories from the catalog...",
    "Checking dimensions, quantities & catalog prices...",
    "Re-rendering your final shoppable room look...",
  ];

  const pollDesign = async (id: string, theaterInterval: NodeJS.Timeout, signal?: AbortSignal) => {
    let attempts = 0;
    while (attempts < 150) { // Allow both image passes and the product selection rounds.
      await new Promise(r => setTimeout(r, 3000)); // wait 3s
      if (signal?.aborted) { clearInterval(theaterInterval); return; }
      try {
        const res = await fetch(`/api/designs/${id}`, { signal });
        const data = await res.json();
        if (data.success && data.design) {
          if (data.design.status === "ready") {
            clearInterval(theaterInterval);
            setDesignResult(data.design);
            setTimeout(() => setStep("reveal"), 600);
            return;
          } else if (data.design.status === "failed") {
            clearInterval(theaterInterval);
            alert(data.design.error || "Generation failed. Please try again.");
            setStep("upload");
            return;
          }
        } else if (res.status === 404) {
          clearInterval(theaterInterval);
          alert("This design could not be found.");
          setStep("upload");
          return;
        }
      } catch (err) {
        if (signal?.aborted) { clearInterval(theaterInterval); return; }
        console.error("Polling error:", err);
      }
      attempts++;
    }
    clearInterval(theaterInterval);
    alert("Your room is taking longer than expected. Refresh this page to check its progress; your design link is saved in the address bar.");
    setStep("upload");
  };

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (id) {
      const controller = new AbortController();
      setStep("generating");
      setTheaterMessageIndex(0);
      const interval = setInterval(() => {
        setTheaterMessageIndex((prev) => (prev + 1) % 5); // 5 theater messages
      }, 2500);
      
      fetch(`/api/designs/${id}`, { signal: controller.signal })
        .then(res => res.json())
        .then(data => {
          if (controller.signal.aborted) return;
          if (data.success && data.design) {
            if (data.design.status === "ready") {
              clearInterval(interval);
              setDesignResult(data.design);
              setStep("reveal");
            } else if (["generating", "queued", "matching"].includes(data.design.status)) {
              pollDesign(id, interval, controller.signal);
            } else {
              clearInterval(interval);
              alert(data.design.error || "Generation failed. Please try again.");
              setStep("upload");
            }
          } else {
            clearInterval(interval);
            alert(data.error || "This design could not be found.");
            setStep("upload");
          }
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          clearInterval(interval);
          setStep("upload");
        });
      return () => { controller.abort(); clearInterval(interval); };
    }
  }, []);

  const handleVerifyInvite = async () => {
    if (!inviteCode.trim()) return;
    try {
      const res = await fetch("/api/verify-invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ inviteCode }),
      });
      const data = await res.json();
      if (data.valid) {
        localStorage.setItem("iva_invite_code", inviteCode);
        setStep("upload");
      } else {
        alert("Invalid or expired invite code.");
      }
    } catch (err) {
      alert("Failed to verify invite code.");
    }
  };

  const computeAndSetAspectRatio = (url: string) => {
    const img = new Image();
    if (url.startsWith("http")) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => {
      const ratio = img.width / img.height;
      const exactDims = [
        { name: "1:1", w: 1024, h: 1024 },
        { name: "16:9", w: 1376, h: 768 },
        { name: "4:3", w: 1280, h: 960 },
        { name: "3:4", w: 960, h: 1280 },
        { name: "9:16", w: 768, h: 1376 },
        { name: "3:2", w: 1344, h: 896 },
        { name: "2:3", w: 896, h: 1344 }
      ];
      
      const closest = exactDims.reduce((prev, curr) => 
        Math.abs((curr.w/curr.h) - ratio) < Math.abs((prev.w/prev.h) - ratio) ? curr : prev
      );
      
      setImageAspectRatio(closest.name);

      const targetW = closest.w;
      const targetH = closest.h;
      
      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      
      if (ctx) {
        // Fit the entire photo so a window or doorway at the edge is never cropped away.
        const scale = Math.min(targetW / img.width, targetH / img.height);
        const drawW = img.width * scale;
        const drawH = img.height * scale;
        const offsetX = (targetW - drawW) / 2;
        const offsetY = (targetH - drawH) / 2;
        
        ctx.fillStyle = "#f4f4f4";
        ctx.fillRect(0, 0, targetW, targetH);
        ctx.drawImage(img, offsetX, offsetY, drawW, drawH);
        try {
          setPhotoPreview(canvas.toDataURL("image/jpeg", 0.9));
        } catch (e) {
          console.error("Canvas taint error, falling back to original", e);
          setPhotoPreview(url);
        }
      } else {
        setPhotoPreview(url);
      }
    };
    img.src = url;
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        const resultUrl = reader.result as string;
        computeAndSetAspectRatio(resultUrl);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSampleRoomSelect = () => {
    const sampleUrl =
      roomType === "bedroom"
        ? "https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?auto=format&fit=crop&w=1376&h=768&q=80"
        : "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1376&h=768&q=80";
    setPhotoPreview(sampleUrl);
    setImageAspectRatio("16:9");
  };

  const startGeneration = async () => {
    let codeToSend = inviteCode;
    if (!codeToSend) {
      codeToSend = localStorage.getItem("iva_invite_code") || "";
      if (!codeToSend) {
        alert("Please enter your invite code first.");
        setStep("invite");
        return;
      }
      setInviteCode(codeToSend);
    }

    setStep("generating");
    setTheaterMessageIndex(0);
    setIsUnlocked(false);

    const interval = setInterval(() => {
      setTheaterMessageIndex((prev) => (prev + 1) % THEATER_MESSAGES.length);
    }, 2500);

    try {
      const res = await fetch("/api/designs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          photoUrl: photoPreview,
          aspectRatio: imageAspectRatio,
          quizAnswers: { ...quizAnswers, roomType },
          inviteCode: codeToSend
        }),
      });

      const data = await res.json();

      if (data.success && data.design) {
        window.history.pushState(null, '', '/design?id=' + data.design.id);
        if (data.design.status === "ready") {
          clearInterval(interval);
          setDesignResult(data.design);
          setTimeout(() => setStep("reveal"), 600);
        } else {
          pollDesign(data.design.id, interval);
        }
      } else {
        clearInterval(interval);
        throw new Error(data.error || "Generation error");
      }
    } catch (err: any) {
      clearInterval(interval);
      console.error(err);
      alert(err.message || "Something went wrong generating your design.");
      setStep("upload");
    }
  };

  const handleUnlockSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!unlockEmail || !unlockEmail.includes("@")) {
      alert("Please enter a valid email address.");
      return;
    }

    setUnlockLoading(true);
    try {
      const res = await fetch("/api/unlock", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          designId: designResult?.id,
          email: unlockEmail,
          marketingConsent,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setIsUnlocked(true);
        setIsUnlockModalOpen(false);
        if (designResult) {
          const unlockedItems = applyPaywallToItems(designResult.items, true);
          setDesignResult({ ...designResult, items: unlockedItems });
        }
      }
    } catch (err) {
      console.error(err);
      setIsUnlocked(true); // Graceful unlock on network glitch
      setIsUnlockModalOpen(false);
      if (designResult) {
        const unlockedItems = applyPaywallToItems(designResult.items, true);
        setDesignResult({ ...designResult, items: unlockedItems });
      }
    } finally {
      setUnlockLoading(false);
    }
  };

  const handleItemSwap = (replacement: Product) => {
    if (!designResult || !swapTargetItem) return;

    const updatedItems = swapDesignItem(designResult.items, swapTargetItem.id, replacement);
    const newTotal = calculateTotalLookPrice(updatedItems);

    setDesignResult({
      ...designResult,
      items: updatedItems,
      total_price_gbp: newTotal,
    });
    setSwapTargetItem(null);
  };

  // Paywalled items computation
  const displayedItems = useMemo(() => {
    if (!designResult) return [];
    return applyPaywallToItems(designResult.items, isUnlocked);
  }, [designResult, isUnlocked]);

  return (
    <div className="flex flex-col flex-1 bg-paper">
      <Header />

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 w-full pb-32">
        
        {/* ========================================================================= */}
        {step === "invite" && (
          <div className="space-y-8 animate-in fade-in duration-300 max-w-md mx-auto text-center pt-10">
            <div>
              <Badge variant="clay" className="mb-4">
                <Lock className="w-3 h-3 text-clay inline mr-1" />
                Private Beta
              </Badge>
              <h1 className="text-3xl sm:text-4xl font-bold text-ink tracking-tight">
                Welcome to Curated Den
              </h1>
              <p className="text-sm text-ink-soft mt-3">
                We are currently in a closed beta to manage AI generation costs. Please enter your invite code to begin your room transformation.
              </p>
            </div>

            <div className="space-y-4">
              <input
                type="text"
                placeholder="Enter Invite Code"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleVerifyInvite();
                  }
                }}
                className="w-full px-4 py-3 bg-paper rounded-xl border border-line text-sm focus:outline-none focus:ring-2 focus:ring-clay/40 text-center"
              />
              <Button 
                type="button"
                size="lg" 
                className="w-full gap-2 cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleVerifyInvite();
                }}
              >
                <span>Enter</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* STEP 1: UPLOAD & ROOM SETUP                                               */}
        {/* ========================================================================= */}
        {step === "upload" && (
          <div className="space-y-8 animate-in fade-in duration-300">
            <div>
              <Badge variant="clay" className="mb-2">
                <Sparkles className="w-3 h-3 text-clay" />
                <span>Step 1 of 2 · Room Details</span>
              </Badge>
              <h1 className="text-3xl sm:text-4xl font-bold text-ink tracking-tight">
                Photograph your space
              </h1>
              <p className="text-sm text-ink-soft mt-1">
                Upload a photo of your room to get a matched, buyable redesign.
              </p>
            </div>

            {/* Room Type Selector */}
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-ink">
                Room Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setRoomType("living");
                    setQuizAnswers((prev) => ({ ...prev, roomType: "living", elementsToAdd: DEFAULT_LIVING_ELEMENTS }));
                  }}
                  className={`py-3 px-4 rounded-xl border text-sm font-semibold transition-all cursor-pointer ${
                    roomType === "living"
                      ? "bg-ink text-paper border-ink shadow-sm"
                      : "bg-paper text-ink border-line hover:bg-paper-deep"
                  }`}
                >
                  Living Room
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRoomType("bedroom");
                    setQuizAnswers((prev) => ({ ...prev, roomType: "bedroom", elementsToAdd: DEFAULT_BEDROOM_ELEMENTS }));
                  }}
                  className={`py-3 px-4 rounded-xl border text-sm font-semibold transition-all cursor-pointer ${
                    roomType === "bedroom"
                      ? "bg-ink text-paper border-ink shadow-sm"
                      : "bg-paper text-ink border-line hover:bg-paper-deep"
                  }`}
                >
                  Bedroom
                </button>
              </div>
            </div>

            {/* Upload Box */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-wider text-ink">
                  Room Photo
                </label>
                <button
                  type="button"
                  onClick={handleSampleRoomSelect}
                  className="text-xs font-semibold text-clay hover:underline cursor-pointer"
                >
                  Use sample {roomType} photo
                </button>
              </div>

              <div className="relative border-2 border-dashed border-line hover:border-clay/60 transition-colors rounded-2xl p-8 text-center bg-paper-deep/50 flex flex-col items-center justify-center min-h-[260px]">
                {photoPreview ? (
                  <div className="relative w-full aspect-video max-h-[300px] overflow-hidden rounded-xl">
                    <img
                      src={photoPreview}
                      alt="Room Preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => setPhotoPreview(null)}
                      className="absolute top-2 right-2 px-3 py-1 bg-ink/80 text-paper text-xs rounded-full cursor-pointer hover:bg-ink"
                    >
                      Change photo
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="w-14 h-14 rounded-full bg-clay/10 text-clay flex items-center justify-center mb-3">
                      <UploadCloud className="w-7 h-7" />
                    </div>
                    <h3 className="text-base font-bold text-ink">
                      Drop your room photo here, or browse
                    </h3>
                    <p className="text-xs text-ink-soft mt-1 max-w-xs">
                      Stand in the doorway in daylight. EXIF & GPS metadata stripped automatically.
                    </p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      aria-label="Upload room photo"
                    />
                  </>
                )}
              </div>
            </div>

            {/* Privacy note */}
            <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-paper-deep border border-line text-xs text-ink-soft">
              <ShieldCheck className="w-4 h-4 text-sage flex-shrink-0 mt-0.5" />
              <p>
                Photos stay private and are automatically deleted after 90 days. Never used for public model training.
              </p>
            </div>

            {/* Next Action */}
            <div className="flex justify-end pt-4">
              <Button
                size="lg"
                onClick={() => {
                  if (!photoPreview) {
                    handleSampleRoomSelect();
                  }
                  setStep("quiz");
                }}
                className="gap-2"
              >
                <span>Continue to Style Quiz</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* STEP 2: 5-QUESTION VISUAL STYLE QUIZ                                      */}
        {/* ========================================================================= */}
        {step === "quiz" && (
          <div className="space-y-8 animate-in fade-in duration-300">
            <div className="flex items-center justify-between">
              <button
                onClick={() => {
                  if (quizStep > 1) setQuizStep((s) => s - 1);
                  else setStep("upload");
                }}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-soft hover:text-ink cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </button>

              <div className="flex items-center gap-1.5">
                {[1, 2, 3, 4, 5].map((q) => (
                  <span
                    key={q}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      quizStep === q
                        ? "bg-clay scale-125"
                        : quizStep > q
                        ? "bg-sage"
                        : "bg-line"
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Q1: Style Aesthetic */}
            {quizStep === 1 && (
              <div className="space-y-6">
                <div>
                  <Badge variant="clay" className="mb-2">Question 1 of 5</Badge>
                  <h2 className="text-2xl sm:text-3xl font-bold text-ink">
                    Which of these feels most like home?
                  </h2>
                  <p className="text-sm text-ink-soft mt-1">
                    Select your primary aesthetic direction.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {STYLES_OPTIONS.map((style) => (
                    <button
                      key={style.id}
                      onClick={() =>
                        setQuizAnswers((prev) => ({
                          ...prev,
                          styleAesthetic: [style.id],
                        }))
                      }
                      className={`p-4 rounded-2xl border text-left transition-all cursor-pointer ${
                        quizAnswers.styleAesthetic.includes(style.id)
                          ? "bg-paper border-clay ring-2 ring-clay/40 shadow-sm"
                          : "bg-paper-deep/60 border-line hover:border-ink-soft"
                      }`}
                    >
                      <h4 className="text-base font-bold text-ink">{style.label}</h4>
                      <p className="text-xs text-ink-soft mt-1">{style.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Q2: Colour Mood */}
            {quizStep === 2 && (
              <div className="space-y-6">
                <div>
                  <Badge variant="clay" className="mb-2">Question 2 of 5</Badge>
                  <h2 className="text-2xl sm:text-3xl font-bold text-ink">
                    Choose your colour mood
                  </h2>
                  <p className="text-sm text-ink-soft mt-1">
                    We'll align furniture finishes and wall paint to this scheme.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {PALETTE_OPTIONS.map((palette) => (
                    <button
                      key={palette.id}
                      onClick={() =>
                        setQuizAnswers((prev) => ({ ...prev, colorMood: palette.id }))
                      }
                      className={`p-5 rounded-2xl border text-left transition-all cursor-pointer ${
                        quizAnswers.colorMood === palette.id
                          ? "bg-paper border-clay ring-2 ring-clay/40 shadow-sm"
                          : "bg-paper-deep/60 border-line hover:border-ink-soft"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        {palette.colors.map((c, i) => (
                          <span
                            key={i}
                            className="w-6 h-6 rounded-full border border-line shadow-xs"
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                      <h4 className="text-base font-bold text-ink">{palette.label}</h4>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Q3: Elements to Add */}
            {quizStep === 3 && (
              <div className="space-y-6">
                <div>
                  <Badge variant="clay" className="mb-2">Question 3 of 4</Badge>
                  <h2 className="text-2xl sm:text-3xl font-bold text-ink">
                    What goes in the room?
                  </h2>
                  <p className="text-sm text-ink-soft mt-1">
                    Select pieces you need for your new space.
                  </p>
                </div>

                <div className="space-y-3">
                  {quizAnswers.elementsToAdd.map((item, idx) => {
                    if (item.name === "Bedding" && !hasBedding) return null;
                    return (
                      <div key={item.name} className="flex items-center gap-3 bg-paper p-3 rounded-xl border border-line">
                        <input
                          type="checkbox"
                          checked={item.selected}
                          onChange={(e) => {
                            const newElements = [...quizAnswers.elementsToAdd];
                            newElements[idx].selected = e.target.checked;
                            setQuizAnswers({ ...quizAnswers, elementsToAdd: newElements });
                          }}
                          className="w-5 h-5 rounded border-line text-clay focus:ring-clay/40"
                        />
                        <span className="text-sm font-semibold text-ink flex-1">{item.name}</span>
                        {item.name === "Bed Frame" && (
                          <select
                            value={item.option || ""}
                            onChange={(e) => {
                              const newElements = [...quizAnswers.elementsToAdd];
                              newElements[idx].option = e.target.value;
                              setQuizAnswers({ ...quizAnswers, elementsToAdd: newElements });
                            }}
                            className="text-xs bg-paper-deep border border-line rounded px-2 py-1 focus:outline-none cursor-pointer"
                          >
                            <option value="Single">Single</option>
                            <option value="Double">Double</option>
                            <option value="King">King</option>
                            <option value="Super King">Super King</option>
                          </select>
                        )}
                        {item.name === "Mattress" && (
                          <select
                            value={item.option || ""}
                            onChange={(e) => {
                              const newElements = [...quizAnswers.elementsToAdd];
                              newElements[idx].option = e.target.value;
                              setQuizAnswers({ ...quizAnswers, elementsToAdd: newElements });
                            }}
                            className="text-xs bg-paper-deep border border-line rounded px-2 py-1 focus:outline-none cursor-pointer"
                          >
                            <option value="Single">Single</option>
                            <option value="Double">Double</option>
                            <option value="King">King</option>
                            <option value="Super King">Super King</option>
                          </select>
                        )}
                        {item.name === "Bedside Table" && (
                          <select
                            value={item.option || ""}
                            onChange={(e) => {
                              const newElements = [...quizAnswers.elementsToAdd];
                              newElements[idx].option = e.target.value;
                              setQuizAnswers({ ...quizAnswers, elementsToAdd: newElements });
                            }}
                            className="text-xs bg-paper-deep border border-line rounded px-2 py-1 focus:outline-none cursor-pointer"
                          >
                            <option value="1">1</option>
                            <option value="2">2</option>
                          </select>
                        )}
                        {item.name === "Sofa" && (
                          <select
                            value={item.option || ""}
                            onChange={(e) => {
                              const newElements = [...quizAnswers.elementsToAdd];
                              newElements[idx].option = e.target.value;
                              setQuizAnswers({ ...quizAnswers, elementsToAdd: newElements });
                            }}
                            className="text-xs bg-paper-deep border border-line rounded px-2 py-1 focus:outline-none cursor-pointer"
                          >
                            <option value="2 seater">2 Seater</option>
                            <option value="3 seater">3 Seater</option>
                            <option value="4 seater">4 Seater</option>
                            <option value="corner">Corner</option>
                          </select>
                        )}
                        {item.name === "Armchair" && (
                          <select
                            value={item.option || ""}
                            onChange={(e) => {
                              const newElements = [...quizAnswers.elementsToAdd];
                              newElements[idx].option = e.target.value;
                              setQuizAnswers({ ...quizAnswers, elementsToAdd: newElements });
                            }}
                            className="text-xs bg-paper-deep border border-line rounded px-2 py-1 focus:outline-none cursor-pointer"
                          >
                            <option value="1">1</option>
                            <option value="2">2</option>
                          </select>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Q4: Life Factors */}
            {quizStep === 4 && (
              <div className="space-y-6">
                <div>
                  <Badge variant="clay" className="mb-2">Question 4 of 4</Badge>
                  <h2 className="text-2xl sm:text-3xl font-bold text-ink">
                    Any household factors?
                  </h2>
                  <p className="text-sm text-ink-soft mt-1">
                    Helps us filter fabric durability and non-permanent fixtures.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {LIFE_FACTORS.map((factor) => {
                    const isSelected = quizAnswers.lifeFactors.includes(factor.id);
                    return (
                      <button
                        key={factor.id}
                        onClick={() => {
                          setQuizAnswers((prev) => ({
                            ...prev,
                            lifeFactors: isSelected
                              ? prev.lifeFactors.filter((f) => f !== factor.id)
                              : [...prev.lifeFactors, factor.id],
                          }));
                        }}
                        className={`p-4 rounded-xl border text-left transition-all cursor-pointer flex items-center justify-between ${
                          isSelected
                            ? "bg-paper border-clay ring-2 ring-clay/40"
                            : "bg-paper-deep/60 border-line hover:border-ink-soft"
                        }`}
                      >
                        <span className="text-sm font-semibold text-ink">{factor.label}</span>
                        {isSelected && <CheckCircle2 className="w-4 h-4 text-clay" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quiz Navigation */}
            <div className="flex justify-end pt-4">
              {quizStep < 4 ? (
                <Button size="lg" onClick={() => setQuizStep((s) => s + 1)} className="gap-2">
                  <span>Next Question</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button size="lg" onClick={startGeneration} className="gap-2">
                  <Sparkles className="w-4 h-4" />
                  <span>Generate My Design</span>
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* STEP 3: GENERATION THEATER STATE                                          */}
        {/* ========================================================================= */}
        {step === "generating" && (
          <div className="py-20 text-center max-w-md mx-auto space-y-6 animate-in fade-in duration-300">
            <div className="relative w-20 h-20 mx-auto">
              <div className="absolute inset-0 rounded-full border-4 border-line border-t-clay animate-spin" />
              <div className="absolute inset-2 rounded-full bg-paper flex items-center justify-center text-clay">
                <Sparkles className="w-6 h-6 animate-pulse" />
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-ink">Reimagining your space...</h2>
              <p className="text-sm text-ink-soft mt-2 font-serif italic transition-all duration-300">
                <TypewriterText text={THEATER_MESSAGES[theaterMessageIndex]} />
              </p>
            </div>

            <div className="text-xs text-ink-soft bg-paper-deep p-3.5 rounded-xl border border-line">
              Matching real products from <span className="font-semibold text-ink">the catalog</span>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* STEP 4: DESIGN REVEAL & SHOPPING LIST (PAYWALLED & UNLOCKED)              */}
        {/* ========================================================================= */}
        {step === "reveal" && designResult && (
          <div className="space-y-10 animate-in fade-in duration-500">
            {/* Header & Restart */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <Badge variant={isUnlocked ? "sage" : "clay"} className="mb-2">
                  {isUnlocked ? "Full Shopping List Unlocked" : "Launch Preview · Hero Items Locked"}
                </Badge>
                <h1 className="text-3xl sm:text-4xl font-bold text-ink tracking-tight">
                  Your Shoppable {designResult.room_type === "bedroom" ? "Bedroom" : "Living Room"}
                </h1>
                <p className="text-sm text-ink-soft mt-1">
                  Shop the selected products. Confirm current prices, sizes and availability with each retailer.
                </p>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setStep("upload");
                  setQuizStep(1);
                  setIsUnlocked(false);
                }}
                className="gap-1.5 self-start sm:self-auto"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Start another room</span>
              </Button>
            </div>

            {/* Before/After Interactive Slider with Tag Overlay */}
            <div>
              <BeforeAfterSlider
                beforeImage={designResult.before_image}
                afterImage={designResult.after_image}
                tags={displayedItems.filter(item => item.render_in_image !== false && item.bbox.w > 0 && item.bbox.h > 0)}
                defaultPosition={50}
                imageAspectRatio={designResult.aspect_ratio}
              />
            </div>

            {/* Total Look Banner */}
            <div className="p-6 rounded-2xl bg-paper-deep border border-line shadow-soft flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-soft">
                  Estimated Shopping Total ({designResult.items.length} product lines)
                </span>
                <div className="text-3xl font-mono font-bold text-brass-deep mt-0.5">
                  {formatGBP(designResult.total_price_gbp)}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Badge variant="sage">Check availability at retailer</Badge>
              </div>
            </div>
            {designResult.notes && <div className="text-xs text-ink-soft space-y-1">
              {designResult.notes.map(note => <p key={note}>{note}</p>)}
            </div>}

            {/* PAYWALL EMAIL CAPTURE CARD (When not unlocked) */}
            {!isUnlocked && (
              <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-5 bg-clay/5 border-2 border-clay/10 rounded-2xl mb-6">
                <div>
                  <h3 className="text-lg font-bold text-ink">Unlock your full shopping list & buy links</h3>
                  <p className="text-xs text-ink-soft mt-1">Hero furniture pieces are currently locked.</p>
                </div>
                <Button onClick={() => setIsUnlockModalOpen(true)} size="md" className="w-full md:w-auto gap-2">
                  <Lock className="w-4 h-4" />
                  <span>Unlock for Free</span>
                </Button>
              </div>
            )}

            {/* Unlock Modal */}
            {isUnlockModalOpen && !isUnlocked && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm animate-in fade-in zoom-in-95">
                <div className="bg-paper w-full max-w-md rounded-2xl shadow-xl overflow-hidden relative">
                  <button
                    onClick={() => setIsUnlockModalOpen(false)}
                    className="absolute top-4 right-4 text-ink-soft hover:text-ink bg-paper-deep rounded-full p-1 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <div className="p-6 sm:p-8">
                    <div className="w-12 h-12 bg-clay/10 rounded-full flex items-center justify-center mb-4 mx-auto">
                      <Lock className="w-6 h-6 text-clay" />
                    </div>
                    <h3 className="text-xl font-bold text-ink text-center mb-2">
                      Get your complete shopping list
                    </h3>
                    <p className="text-sm text-ink-soft text-center mb-6">
                      Enter your email to immediately reveal all SKU titles, dimensions, and direct purchase links for this design.
                    </p>
                    
                    <form onSubmit={handleUnlockSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <input
                          type="email"
                          required
                          placeholder="Email address"
                          value={unlockEmail}
                          onChange={(e) => setUnlockEmail(e.target.value)}
                          className="w-full px-4 py-3 bg-paper-deep rounded-xl border border-line text-sm focus:outline-none focus:ring-2 focus:ring-clay/40 transition-shadow"
                        />
                      </div>
                      <label className="flex items-start gap-2 text-[11px] text-ink-soft cursor-pointer">
                        <input
                          type="checkbox"
                          checked={marketingConsent}
                          onChange={(e) => setMarketingConsent(e.target.checked)}
                          className="mt-0.5 rounded border-line text-clay focus:ring-clay/40"
                        />
                        <span>Send me occasional design updates and trade decor guides (optional)</span>
                      </label>
                      <Button type="submit" size="lg" disabled={unlockLoading} className="w-full gap-2 mt-2">
                        <Unlock className="w-4 h-4" />
                        <span>{unlockLoading ? "Unlocking..." : "Unlock Now (Free)"}</span>
                      </Button>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Shoppable Items List */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-ink">
                  {isUnlocked ? "Full Shoppable Product Breakdown" : "Shopping List (Preview)"}
                </h3>
                <span className="text-xs text-ink-soft">Prices include VAT</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {displayedItems.map((item) => (
                  <div
                    key={item.id}
                    className={`bg-paper p-4 rounded-xl border transition-all flex items-start gap-3.5 ${
                      item.locked
                        ? "border-line/80 bg-paper-deep/40 opacity-90"
                        : "border-line shadow-soft hover:shadow-card"
                    }`}
                  >
                    {item.image_url ? (
                      <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-paper-deep flex-shrink-0">
                        <img
                          src={item.image_url || undefined}
                          alt={item.label}
                          className="w-full h-full object-cover"
                        />
                        {item.locked && (
                          <div className="absolute inset-0 bg-ink/40 backdrop-blur-[2px] flex items-center justify-center text-paper">
                            <Lock className="w-5 h-5" />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-paper-deep flex items-center justify-center text-ink-soft flex-shrink-0">
                        <Tag className="w-6 h-6" />
                      </div>
                    )}

                    <div className="flex-1 min-w-0 flex flex-col justify-between h-full">
                      <div>
                        <div className="flex items-center justify-between text-[11px] text-ink-soft">
                          <span className="font-bold uppercase tracking-wider text-clay">
                            {item.category.replace(/_/g, " ")}
                          </span>
                          <span>{item.retailer}</span>
                        </div>

                        {item.locked ? (
                          <div className="mt-1">
                            <div className="h-4 bg-line/60 rounded w-3/4 animate-pulse mb-1" />
                            <span className="text-[11px] text-ink-soft italic flex items-center gap-1">
                              <Lock className="w-3 h-3 text-clay" />
                              <span>Unlocked via email</span>
                            </span>
                          </div>
                        ) : (
                          <h4 className="text-sm font-semibold text-ink mt-0.5 truncate">
                            {item.product_title || item.label}
                          </h4>
                        )}
                      </div>

                      {item.render_in_image === false && <p className="mt-1 text-xs text-ink-soft">Shopping list only · covered by bed linen in the design</p>}
                      {item.category === "paint" && item.product?.volume_litres && <p className="mt-1 text-xs text-ink-soft">{item.product.volume_litres}L tin · check coverage before ordering</p>}
                      {item.product?.price_needs_size_check && <p className="mt-1 text-xs text-ink-soft">{item.product.size ? `${item.product.size} selected · ` : ""}confirm size price at retailer</p>}

                      <div className="mt-2 pt-2 border-t border-line/60 flex items-center justify-between">
                        <span className="text-sm font-mono font-bold text-brass-deep">
                          {formatGBP(item.price_gbp * (item.quantity ?? 1))}
                          {(item.quantity ?? 1) > 1 && <span className="block text-[11px] font-normal text-ink-soft">{item.quantity} × {formatGBP(item.price_gbp)}</span>}
                        </span>

                        {item.locked ? (
                          <span className="text-[11px] font-medium text-ink-soft">Locked item</span>
                        ) : (
                          <div className="flex items-center gap-3">
                            {item.alternatives && item.alternatives.length > 0 && (
                              <button
                                onClick={() => setSwapTargetItem(item)}
                                className="text-xs font-semibold text-ink-soft hover:text-ink cursor-pointer flex items-center gap-1"
                              >
                                <RefreshCw className="w-3 h-3" />
                                <span>Swap</span>
                              </button>
                            )}

                            <a
                              href={item.product ? `/api/out/${encodeURIComponent(item.product.id)}` : item.affiliate_url}
                              target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 bg-ink text-paper text-[10px] font-bold px-3 py-1.5 rounded-full hover:bg-ink/90 transition-colors"
                            >
                              <span>Buy Now</span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* ITEM SWAP MODAL DRAWER                                                    */}
        {/* ========================================================================= */}
        {swapTargetItem && (
          <div className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
            <div className="bg-paper rounded-3xl border border-line max-w-lg w-full p-6 shadow-card space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <Badge variant="clay">Swap Recommendation</Badge>
                  <h3 className="text-lg font-bold text-ink mt-1">
                    Select alternative {swapTargetItem.category}
                  </h3>
                </div>
                <button
                  onClick={() => setSwapTargetItem(null)}
                  className="w-8 h-8 rounded-full bg-paper-deep hover:bg-line text-ink flex items-center justify-center cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                {swapTargetItem.alternatives?.map((alt) => (
                  <div
                    key={alt.id}
                    className="p-3.5 rounded-2xl border border-line bg-paper hover:bg-paper-deep flex items-center justify-between gap-3 transition-colors"
                  >
                    <img
                      src={alt.image_url || undefined}
                      alt={alt.title}
                      className="w-16 h-16 rounded-xl object-cover bg-paper-deep flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs font-bold text-ink line-clamp-2">{alt.title}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-mono text-xs font-bold text-brass-deep">
                          {formatGBP(alt.price_gbp)}
                        </span>
                        {alt.color && (
                          <span className="text-[10px] text-ink-soft">({alt.color})</span>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleItemSwap(alt)}
                      className="whitespace-nowrap"
                    >
                      Choose
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
